import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'

export type SidebarMode = 'list' | 'detail'

export type DetailTarget =
  | { kind: 'section'; sectionId: string }
  | { kind: 'style_group'; group: 'colors' | 'fonts' | 'spacing' | 'buttons' }
  | { kind: 'branding_group'; group: 'logo' | 'social' }

export const useSidebarStore = defineStore('sidebar', () => {
  const mode = ref<SidebarMode>('list')
  const detailTarget = ref<DetailTarget | null>(null)
  const isCollapsed = useLocalStorage('pageBuilderSidebarCollapsed', false)
  const highlightFieldKey = ref<string | null>(null)
  const highlightFieldKeys = ref<string[]>([])
  const highlightRowKeys = ref<string[]>([])

  function pulseKeys(target: typeof highlightFieldKeys, keys: string[], duration: number) {
    const uniqueKeys = [...new Set(keys.filter(Boolean))]
    if (uniqueKeys.length === 0) {
      return
    }

    target.value = [...new Set([...target.value, ...uniqueKeys])]

    for (const key of uniqueKeys) {
      setTimeout(() => {
        target.value = target.value.filter((current) => current !== key)
      }, duration)
    }
  }

  function drillTo(target: DetailTarget) {
    detailTarget.value = target
    mode.value = 'detail'
  }

  function back() {
    mode.value = 'list'
    setTimeout(() => {
      detailTarget.value = null
    }, 300)
  }

  function highlightField(fieldKey: string) {
    highlightFieldKey.value = fieldKey
    pulseKeys(highlightFieldKeys, [fieldKey], 3000)
    setTimeout(() => {
      if (highlightFieldKey.value === fieldKey) {
        highlightFieldKey.value = null
      }
    }, 3000)
  }

  function highlightFields(fieldKeys: string[], duration = 3000) {
    pulseKeys(highlightFieldKeys, fieldKeys, duration)
  }

  function highlightRow(rowKey: string, duration = 2000) {
    pulseKeys(highlightRowKeys, [rowKey], duration)
  }

  function highlightRows(rowKeys: string[], duration = 2000) {
    pulseKeys(highlightRowKeys, rowKeys, duration)
  }

  function toggleCollapse() {
    isCollapsed.value = !isCollapsed.value
  }

  return {
    mode,
    detailTarget,
    isCollapsed,
    highlightFieldKey,
    highlightFieldKeys,
    highlightRowKeys,
    drillTo,
    back,
    highlightField,
    highlightFields,
    highlightRow,
    highlightRows,
    toggleCollapse,
  }
})
